/**
 * Commodity Settings card — CR-8710.
 *
 * The Supplier Performance and Dashboard commodity filters read the commodity
 * on the supplier's relationship with the site being viewed, so this card
 * reads and writes that, not the supplier record. A save applies to every
 * site in the top-bar view (one site, or every site of a company or group the
 * user can access), as the Supplier Portal does; the card shows the first of
 * them. Customer admins only; everyone else sees it read only.
 */

import { useState, useEffect, useCallback } from 'react';
import { coreAPI, purchasingAPI, fetchAllPages, apiErrorMessage, CommodityGroup, CommodityType, SupplierCommodity, ViewScopeParams } from '../../lib/api';
import { useViewGroup, useViewSite } from '../../contexts/ViewContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Layers, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface CommoditySettingsCardProps {
  /** The supplier's company id. */
  supplierCompanyId: number;
}

const SELECT_CLASS = 'w-full px-3 py-2 border rounded-md text-sm bg-background disabled:cursor-not-allowed';
const LABEL_CLASS = 'text-xs font-medium text-muted-foreground uppercase tracking-wider';

export default function CommoditySettingsCard({ supplierCompanyId }: CommoditySettingsCardProps) {
  const siteId = useViewSite();
  const group = useViewGroup();
  const groupId = group?.id ?? null;
  const [groups, setGroups] = useState<CommodityGroup[]>([]);
  const [types, setTypes] = useState<CommodityType[]>([]);
  const [current, setCurrent] = useState<SupplierCommodity | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const scope = useCallback((): ViewScopeParams => {
    if (siteId) return { site: siteId };
    if (groupId) return { group: groupId };
    return {};
  }, [siteId, groupId]);

  useEffect(() => {
    let live = true;
    Promise.all([
      purchasingAPI.getCommodityGroups({ page_size: 100 }),
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      fetchAllPages<CommodityType>((page) => purchasingAPI.getCommodityTypes({ page_size: 100, page })),
    ])
      .then(([gRes, allTypes]) => {
        if (!live) return;
        const gData = gRes.data;
        setGroups(Array.isArray(gData) ? gData : (gData as Record<string, unknown>)['results'] as CommodityGroup[]);
        setTypes(allTypes);
      })
      .catch((err: unknown) => { console.error('Error loading commodities:', err); });
    return () => { live = false; };
  }, []);

  // Re-read whenever the top-bar view moves: each site holds its own value.
  useEffect(() => {
    let live = true;
    setLoading(true);
    coreAPI.getSupplierCommodity(supplierCompanyId, scope())
      .then((res) => { if (live) setCurrent(res.data); })
      .catch((err: unknown) => { console.error('Error loading commodity settings:', err); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [supplierCompanyId, scope]);

  const save = async (commodityGroup: number | null, commodityType: number | null) => {
    setSaving(true);
    try {
      const res = await coreAPI.setSupplierCommodity(
        { supplier: supplierCompanyId, commodity_group: commodityGroup, commodity_type: commodityType }, scope());
      setCurrent(res.data);
      toast.success('Commodity settings updated');
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Failed to update commodity settings'));
    } finally {
      setSaving(false);
    }
  };

  const toId = (value: string) => (value ? parseInt(value, 10) : null);
  const canEdit = !!current?.can_edit;
  const selectedGroup = current?.commodity_group ?? null;
  const siteCount = current?.site_count ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Layers className="h-5 w-5" />Commodity Settings</CardTitle>
        <CardDescription>
          Assign commodity group and type classification for this supplier
          {siteCount > 1 ? ` — saved to all ${siteCount} sites in the current view` : ''}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="commodity-group" className={LABEL_CLASS}>Commodity Group</label>
            <select
              id="commodity-group"
              value={selectedGroup ?? ''}
              // Clearing or changing the Group clears the Type.
              onChange={(e) => save(toId(e.target.value), null)}
              className={SELECT_CLASS}
              disabled={!canEdit || saving || loading}
            >
              <option value="">None</option>
              {/* A stored group outside the tenant's list still shows by name. */}
              {selectedGroup && !groups.some((g) => g.id === selectedGroup) && (
                <option value={selectedGroup}>{current?.commodity_group_name ?? selectedGroup}</option>
              )}
              {groups.map((g) => (<option key={g.id} value={g.id}>{g.name}</option>))}
            </select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="commodity-type" className={LABEL_CLASS}>Commodity Type</label>
            <select
              id="commodity-type"
              value={current?.commodity_type ?? ''}
              onChange={(e) => save(selectedGroup, toId(e.target.value))}
              className={SELECT_CLASS}
              disabled={!canEdit || saving || loading || !selectedGroup}
            >
              <option value="">None</option>
              {current?.commodity_type && !types.some((t) => t.id === current.commodity_type) && (
                <option value={current.commodity_type}>{current.commodity_type_name ?? current.commodity_type}</option>
              )}
              {types.filter((t) => t.commodity_group === selectedGroup).map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
            </select>
          </div>
        </div>
        {saving && (<div className="flex items-center gap-2 text-sm text-muted-foreground mt-3"><Loader2 className="h-4 w-4 animate-spin" />Saving...</div>)}
        {!loading && current && siteCount === 0 && (
          <p className="text-sm text-muted-foreground mt-3">This supplier has no relationship with the sites in the current view.</p>
        )}
        {!loading && current && siteCount > 0 && !canEdit && (
          <p className="text-sm text-muted-foreground mt-3">Only a customer admin can change Commodity Settings.</p>
        )}
      </CardContent>
    </Card>
  );
}
